import { Howler } from 'howler';

/**
 * `AudioContextState` plus Safari's non-standard `'interrupted'` (iOS, while the
 * audio session is taken away), which the TS DOM lib omits.
 */
export type tAudioContextState = AudioContextState | 'interrupted';

/**
 * Read defensively on purpose: Howler creates the context lazily, leaves it
 * undefined without WebAudio, closes it on `Howler.unload()`, and tests mock it.
 */
function getContext(): AudioContext | undefined {
  const howler = Howler as unknown as { ctx?: unknown } | undefined;
  const ctx = howler?.ctx;

  if (typeof ctx !== 'object' || ctx === null) return undefined;

  return ctx as AudioContext;
}

export function getHowlerContextState(): tAudioContextState | undefined {
  const state = (getContext() as { state?: unknown } | undefined)?.state;

  return typeof state === 'string' ? (state as tAudioContextState) : undefined;
}

/**
 * Repair a WebAudio context the OS left suspended. On iOS an interruption or a
 * background spell can leave the shared context `suspended`/`interrupted` with
 * nothing to bring it back — Howler's `_autoResume()` only covers the paths it
 * initiates — and every play into it is silent. Call once per foreground.
 */
export function resumeHowlerContext(): void {
  const ctx = getContext();

  if (!ctx) return;

  const state = getHowlerContextState();

  if (state !== 'suspended' && state !== 'interrupted') return;

  const { resume } = ctx as { resume?: unknown };

  if (typeof resume !== 'function') return;

  try {
    const result: unknown = (resume as () => unknown).call(ctx);

    // resume() rejects until the next user gesture; unhandled, that is Sentry noise.
    if (
      typeof result === 'object' &&
      result !== null &&
      typeof (result as Promise<void>).catch === 'function'
    ) {
      (result as Promise<void>).catch(() => undefined);
    }
  } catch (error) {
    // InvalidStateError on a closed context: no context left to repair.
  }
}
